import { setCookie } from "cookies-next";

// external dependencies
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";

// types

import { COOKIE_CONFIG } from "@/config";
import { GLOBAL_ERROR_MESSAGE } from "@/constants/validation-message";
import { IApiError } from "@/interface/apiError.types";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";

// internal utilities and services
import { loginUser } from "@/services/auth.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { loginSchema } from "@/schema/auth/login";

const useLogin = () => {
  const router = useRouter();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const { mutate: login, isPending: loginLoading } = useMutation({
    mutationFn: loginUser,
    onSuccess: (data) => {
      setCookie(COOKIE_CONFIG.loggedIn, "true");
      showToast(TOAST_TYPES.success, data?.message ?? "Login successful");
      router.refresh();
    },

    onError: (error: IApiError) => {
      console.error("Login error:", error);

      const errors = error?.response?.data;
      showToast(TOAST_TYPES.error, errors?.message ?? GLOBAL_ERROR_MESSAGE);
    },
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    login(values);
  };

  return {
    form,
    onSubmit,
    loginLoading,
    showPassword,
    togglePasswordVisibility,
  };
};

export default useLogin;
